import moment from 'moment';
import {describeApiOutcome} from '@/utils/apiOutcome';

export const CLIENT_USER_STATUS = {
    INVITED: 'invited',
    ACTIVE: 'active',
    INACTIVE: 'inactive',
};

const STATUS_LABELS = {
    [CLIENT_USER_STATUS.INVITED]: 'Uitgenodigd',
    [CLIENT_USER_STATUS.ACTIVE]: 'Actief',
    [CLIENT_USER_STATUS.INACTIVE]: 'Inactief',
};

const STATUS_BADGES = {
    [CLIENT_USER_STATUS.INVITED]: 'badge-warning',
    [CLIENT_USER_STATUS.ACTIVE]: 'badge-success',
    [CLIENT_USER_STATUS.INACTIVE]: 'badge-secondary',
};

const LAST_LOGIN_FORMAT = 'DD-MM-YYYY HH:mm';

export const NEVER_LOGGED_IN = 'Nog niet ingelogd';

export const NO_PHONE_NUMBER = '-';

export const FORGOTTEN_PASSWORD_HINT = 'Wachtwoord kwijt? De gebruiker vraagt zelf een nieuw aan via "Wachtwoord vergeten" op het inlogscherm.';

export const CLIENT_USERS_UNAVAILABLE = 'De klantgebruikers konden niet worden opgehaald.';

export const ACCESS_CHANGE_FAILED = 'De toegang van deze klantgebruiker kon niet worden aangepast.';

export const INVITATION_NOT_RESENT = 'De uitnodiging kon niet opnieuw worden verstuurd.';

const LAST_ACTIVE_USER_WARNING = 'Dit is de laatste actieve gebruiker van deze klant: daarna kan niemand aan klantzijde meer bij de uren en verlofaanvragen.';

export function statusLabel(clientUser) {
    return STATUS_LABELS[clientUser.status] || clientUser.status;
}

export function statusBadge(clientUser) {
    return STATUS_BADGES[clientUser.status] || 'badge-light';
}

export function formatLastLogin(clientUser) {
    return clientUser.lastPortalLoginAt
        ? moment(clientUser.lastPortalLoginAt).format(LAST_LOGIN_FORMAT)
        : NEVER_LOGGED_IN;
}

export function phoneNumberOf(clientUser) {
    return clientUser.phone || NO_PHONE_NUMBER;
}

export function canBeDeactivated(clientUser) {
    return clientUser.status !== CLIENT_USER_STATUS.INACTIVE;
}

export function canBeReactivated(clientUser) {
    return clientUser.status === CLIENT_USER_STATUS.INACTIVE;
}

export function canBeReinvited(clientUser) {
    return clientUser.status === CLIENT_USER_STATUS.INVITED;
}

export function pointsToForgottenPassword(clientUser) {
    return clientUser.status === CLIENT_USER_STATUS.ACTIVE;
}

export function isLastActiveClientUser(clientUsers, clientUser) {
    return clientUser.status === CLIENT_USER_STATUS.ACTIVE
        && !clientUsers.some(other => other.id !== clientUser.id && other.status === CLIENT_USER_STATUS.ACTIVE);
}

export function deactivationConfirmation(clientUsers, clientUser) {
    const consequence = clientUser.name + ' (' + clientUser.email + ') kan daarna niet meer inloggen op het portaal.';

    return isLastActiveClientUser(clientUsers, clientUser)
        ? consequence + ' ' + LAST_ACTIVE_USER_WARNING
        : consequence;
}

export function deactivatedMessage(clientUser) {
    return clientUser.name + ' is op inactief gezet.';
}

export function reactivatedMessage(clientUser) {
    return clientUser.status === CLIENT_USER_STATUS.INVITED
        ? clientUser.name + ' is weer geactiveerd. Verstuur de uitnodiging opnieuw, zodat een wachtwoord kan worden ingesteld.'
        : clientUser.name + ' is weer actief en kan inloggen met het bestaande wachtwoord.';
}

export function reinvitedMessage(clientUser, outcome) {
    return outcome.message || 'De uitnodiging is opnieuw verstuurd naar ' + clientUser.email + '.';
}

export function replaceClientUser(clientUsers, changed) {
    return clientUsers.map(clientUser => (clientUser.id === changed.id ? changed : clientUser));
}

function listedClientUsers(clientUsers) {
    return {ok: true, clientUsers, message: null};
}

export function noClientUsersYet() {
    return listedClientUsers([]);
}

export function describeClientUsersOutcome(result) {
    const outcome = describeApiOutcome(result, {fallback: CLIENT_USERS_UNAVAILABLE});
    const clientUsers = outcome.ok && outcome.data && outcome.data.data;

    if (!Array.isArray(clientUsers)) {
        return {ok: false, clientUsers: [], message: outcome.message || CLIENT_USERS_UNAVAILABLE};
    }

    return listedClientUsers(clientUsers);
}

export function describeAccessChangeOutcome(result) {
    const outcome = describeApiOutcome(result, {fallback: ACCESS_CHANGE_FAILED});
    const clientUser = outcome.ok && outcome.data && outcome.data.data;

    if (!clientUser || typeof clientUser !== 'object') {
        return {ok: false, clientUser: null, message: outcome.message || ACCESS_CHANGE_FAILED};
    }

    return {ok: true, clientUser, message: null};
}

export function describeReinvitationOutcome(result) {
    const outcome = describeApiOutcome(result, {fallback: INVITATION_NOT_RESENT});

    return {
        ok: outcome.ok,
        message: outcome.ok ? (outcome.data && outcome.data.message) || null : outcome.message,
    };
}
